/**
 * Open a case by hand — the fourth of FR-E2's four origins.
 *
 * The other three (the compliance calendar, an accepted request, an accepted
 * proposal) never come near this form; they call the same opener on the server.
 *
 * Three things here are decisions rather than layout.
 *
 * **The service list is not filtered by the client's kind.** `applies_to`
 * (ADR-0015) *filters what is offered* on the public site and in the portal; it
 * does not stop staff opening a case. A firm doing an unusual piece of work for
 * an unusual client is not wrong, and a form that refused them would be.
 *
 * **The period is optional and its shapes are served.** `MM-YYYY`, `YYYY-YY`,
 * `AY YYYY-YY` come from `/vocabulary`, so the form and the one function that
 * validates them cannot drift.
 *
 * **`USAGE_LIMIT_EXCEEDED` is not an error.** It is a commercial fact and comes
 * with a way forward — the shape `NewClientDialog` set for the clients meter.
 *
 * **The plan picker is w5a's, and it is the field whose absence made the whole
 * billing chain unreachable.** `POST /api/v1/cases` has accepted `plan_id`
 * since Wave 3A and nothing in the product sent one, so every case a firm
 * opened carried `plan_id = NULL` — which meant `raise_invoice` could only ever
 * record `{"skipped": "no_plan"}`, no invoice could reach a case's timeline,
 * and hours pricing had nothing to price against (the Wave 4 close, §5.5). The
 * plans come from the service the firm just chose: `GET /api/v1/services`
 * already returns each service with its plans **and their prices** in one read,
 * so this costs no request. Choosing the service again clears the plan, because
 * a plan belongs to one service and the server refuses a mismatch.
 *
 * **C27 (UAT reports #13, #19, #25).** Client and service are marked required
 * with the red star, their *Choose…* is a placeholder rather than a choice, and
 * pressing *Open case* with either missing says so under the field — the
 * button used to grey out with a tooltip nobody on a phone could read. The
 * plan cap's link goes to the website's pricing page for this app
 * (`plansUrl`), not the store's `/apps/<slug>`, which the store deleted.
 */

import { Alert, Button, Dialog, Input, Select } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { type ClientRecord, clients } from '../../lib/api/clients'
import { type CaseVocabulary, type Priority, PRIORITY_LABELS, cases } from '../../lib/api/cases'
import { type Service, priceLabel, services } from '../../lib/api/services'
import { APP_SLUG, plansUrl } from '../../lib/config'
import { DialogActions, FORM_GRID, FormRow } from '../../components/page'

export function NewCaseDialog({
  onClose,
  onOpened,
  clientId,
}: {
  onClose: () => void
  onOpened: (caseId: string) => void
  clientId?: string
}) {
  const [clientList, setClientList] = useState<ClientRecord[]>([])
  const [serviceList, setServiceList] = useState<Service[]>([])
  const [vocab, setVocab] = useState<CaseVocabulary | null>(null)
  const [draft, setDraft] = useState({
    client_id: clientId ?? '',
    service_id: '',
    plan_id: '',
    period: '',
    title: '',
    priority: 'normal',
    due_date: '',
  })
  const [error, setError] = useState<{ message: string; atCap: boolean } | null>(null)
  const [saving, setSaving] = useState(false)
  /** Open case was pressed — from then on a missing required field says so. */
  const [tried, setTried] = useState(false)

  useEffect(() => {
    void Promise.all([clients.list({ status: 'active', limit: 200 }), services.list(), cases.vocabulary()])
      .then(([c, s, v]) => {
        setClientList(c.data)
        setServiceList(s.data.services)
        setVocab(v.data)
      })
      .catch(() => setError({ message: 'The form could not be loaded.', atCap: false }))
  }, [])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setTried(true)
    if (!draft.client_id || !draft.service_id) return
    setSaving(true)
    setError(null)
    try {
      const body = await cases.open({
        client_id: draft.client_id,
        service_id: draft.service_id,
        // A case may legitimately have no plan — bespoke work a firm prices
        // when it is finished — so the empty option is *not sent* rather than
        // sent empty, the same rule the period below follows.
        plan_id: draft.plan_id || undefined,
        // `|| undefined`, so an untouched box is *not sent* rather than sent
        // empty — the service treats an empty period as "no period", and the
        // two must not become different requests.
        period: draft.period.trim() || undefined,
        title: draft.title.trim() || undefined,
        priority: draft.priority as 'low' | 'normal' | 'high' | 'urgent',
        due_date: draft.due_date || undefined,
      })
      onOpened(body.data.id)
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ message: err.message, atCap: err.code === 'USAGE_LIMIT_EXCEEDED' })
      } else {
        setError({ message: 'The case could not be opened.', atCap: false })
      }
    } finally {
      setSaving(false)
    }
  }

  // The chosen service's plans, from the list already loaded. Archived plans
  // are dropped: the server refuses one (`domain/cases._resolve`) and offering
  // it would be a picker whose choices are refused after the click.
  const plans = (serviceList.find((s) => s.id === draft.service_id)?.plans ?? []).filter(
    (plan) => plan.status === 'active',
  )

  const clientMissing = tried && !draft.client_id ? 'Choose the client this case is for.' : null
  const serviceMissing = tried && !draft.service_id ? 'Choose the service.' : null

  return (
    // The kit's dialog (2026-09-25): the panel is bounded by the window and its
    // body scrolls, so the title and close stay reachable. A plain failure is
    // the dialog's pinned error; the plan cap carries a link and a warning
    // tone, so it stays in the body as it was. Laid out as the Branches dialog
    // is since Wave 15C (C14 §10.6 item 7).
    <Dialog
      open
      onClose={onClose}
      title="Open a case"
      error={error && !error.atCap ? error.message : null}
      width="lg"
    >
      {/* `noValidate`: the browser's own bubble would stop the submit before
          this form could say which field is missing, under the field. */}
      <form onSubmit={submit} noValidate>
        {error?.atCap && (
          <div className="mb-4">
            <Alert tone="warning">
              {error.message}{' '}
              <a
                className="underline"
                href={plansUrl(APP_SLUG)}
                target="_blank"
                rel="noreferrer"
              >
                Review your plan
              </a>
            </Alert>
          </div>
        )}

        <div className={FORM_GRID}>
          <FormRow label="Client" htmlFor="new-case-client" required>
            <Select
              id="new-case-client"
              value={draft.client_id}
              onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
              required
              aria-required="true"
              disabled={Boolean(clientId)}
              error={clientMissing}
            >
              <option value="" disabled hidden>
                Choose…
              </option>
              {clientList.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.display_name || client.legal_name}
                </option>
              ))}
            </Select>
          </FormRow>

          <FormRow label="Service" htmlFor="new-case-service" required>
            <Select
              id="new-case-service"
              value={draft.service_id}
              // The plan goes with the service. Keeping a plan id across a
              // service change would send the server a pair it refuses, and the
              // refusal would name a field the firm cannot see.
              onChange={(e) => setDraft({ ...draft, service_id: e.target.value, plan_id: '' })}
              required
              aria-required="true"
              error={serviceMissing}
              hint="The case opens on the service’s published workflow and stays on that version, whatever you change later."
            >
              <option value="" disabled hidden>
                Choose…
              </option>
              {serviceList.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Select>
          </FormRow>

          <FormRow label="Plan" htmlFor="new-case-plan" optional>
            <Select
              id="new-case-plan"
              value={draft.plan_id}
              onChange={(e) => setDraft({ ...draft, plan_id: e.target.value })}
              disabled={!draft.service_id}
              hint={
                !draft.service_id
                  ? 'Choose a service first — plans belong to a service.'
                  : plans.length === 0
                    ? 'This service has no plans yet. Add one in Services, or leave the case unpriced and invoice it by hand.'
                    : 'What the case is billed on. Without a plan an invoice can only be raised by hand — a stage that raises one automatically will skip.'
              }
            >
              <option value="">No plan</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} — {priceLabel(plan)}
                </option>
              ))}
            </Select>
          </FormRow>

          <FormRow label="Period" htmlFor="new-case-period" optional>
            <Input
              id="new-case-period"
              value={draft.period}
              onChange={(e) => setDraft({ ...draft, period: e.target.value })}
              placeholder="04-2026"
              hint={(vocab?.period_shapes ?? ['MM-YYYY', 'YYYY-YY', 'AY YYYY-YY']).join(' · ')}
            />
          </FormRow>

          <FormRow label="Title" htmlFor="new-case-title" optional>
            <Input
              id="new-case-title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={200}
              hint="Left blank, the case is called by its service and period."
            />
          </FormRow>

          <FormRow label="Priority" htmlFor="new-case-priority">
            <Select
              id="new-case-priority"
              value={draft.priority}
              onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
            >
              {(vocab?.priorities ?? ['low', 'normal', 'high', 'urgent']).map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value as Priority] ?? value}
                </option>
              ))}
            </Select>
          </FormRow>

          <FormRow label="Due" htmlFor="new-case-due" optional>
            <Input
              id="new-case-due"
              type="date"
              value={draft.due_date}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
            />
          </FormRow>
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} loading={saving} loadingText="Opening…">
            Open case
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
